import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type MetricDefinition } from "@/components/admin/XeroMetricInfo";
import XeroPanel, { SCROLLER } from "@/components/admin/XeroPanel";
import XeroRecentRunsDrawer from "@/components/admin/XeroRecentRunsDrawer";
import { StatusBadge } from "@/components/admin/XeroRunDisplay";
import { formatNumber, formatWhen } from "@/lib/xeroRunFormat";
import { useXeroOrgName } from "@/hooks/useXeroOrgName";
import {
  XERO_ORCHESTRATION_QUERY_KEY,
  getXeroOrchestration,
  type XeroRun,
  type XeroWatermark,
} from "@/services/xeroApi";

const RUN_HISTORY_SHOWN = 20;

const METRICS: MetricDefinition[] = [
  {
    term: "Watermark",
    definition:
      "The highest Xero journal number already loaded for that organisation. It is the whole resume mechanism: each run asks Xero for journals above this number, so only new ones are downloaded. A posted journal is immutable in Xero — a correction is a new journal with a new number — which is what makes the number sufficient on its own.",
  },
  {
    term: "Last run",
    definition:
      "The outcome of the most recent attempt for that organisation, whatever it was. A failure shows here without moving the watermark.",
  },
  {
    term: "When",
    definition: "When that most recent attempt started.",
  },
  {
    term: "Rows",
    definition:
      "Rows written by the most recent attempt — not a running total for the organisation. For rows currently held, see the Row counts tab.",
  },
  {
    term: "Access token",
    definition:
      "Xero access tokens live 30 minutes. A lapsed one is not a fault: the next run refreshes it before pulling. It explains most pipeline failures when it cannot be refreshed.",
  },
];


function TokenState({
  expiresIn,
  expiresAt,
}: {
  expiresIn: number;
  expiresAt: string | null;
}) {
  if (!expiresAt) return null;
  const minutes = Math.floor(expiresIn / 60);
  const lapsed = expiresIn <= 0;
  return (
    <span
      className={`flex items-center gap-1.5 text-xs ${
        lapsed ? "text-amber-700" : "text-muted-foreground"
      }`}
      title={`Access token expires ${new Date(expiresAt).toLocaleString()}`}
    >
      <Clock className="h-3.5 w-3.5" />
      {lapsed
        ? "Access token expired — the next run will refresh it"
        : `Access token valid for ${minutes} min`}
    </span>
  );
}

/** Where each organisation's load has got to. */
export default function XeroWatermarkPanel() {
  const orgName = useXeroOrgName();
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: XERO_ORCHESTRATION_QUERY_KEY,
    queryFn: () => getXeroOrchestration("journals"),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading journal load history…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {(error as Error)?.message ?? "Couldn’t load the journal load history."}
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const watermarks: XeroWatermark[] = data?.watermarks ?? [];
  const runs: XeroRun[] = (data?.recent_runs ?? []).slice(0, RUN_HISTORY_SHOWN);

  return (
    <XeroPanel
      title="Watermarks"
      blurb="Where each organisation's journal load has got to. Runs started from the Fabric workspace report here too."
      metrics={METRICS}
      aside={
        <>
          <TokenState
            expiresIn={data?.expires_in ?? 0}
            expiresAt={data?.expires_at ?? null}
          />
          <XeroRecentRunsDrawer runs={runs} />
        </>
      }
      onRefresh={() => refetch()}
      isFetching={isFetching}
    >
      {watermarks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No journal loads recorded yet. The Fabric pipeline reports here the
          first time it runs.
        </div>
      ) : (
        <div className={SCROLLER}>
          <Table>
            <TableHeader>
              <TableRow className="sticky top-0 z-10 bg-muted hover:bg-muted">
                <TableHead>Organisation</TableHead>
                <TableHead className="w-[120px]">Last run</TableHead>
                <TableHead className="w-[180px]">When</TableHead>
                <TableHead className="w-[120px] text-right">Rows</TableHead>
                <TableHead className="w-[140px] text-right">Watermark</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {watermarks.map((watermark) => (
                <TableRow key={watermark.tenant_id}>
                  <TableCell className="font-medium text-foreground">
                    {orgName(watermark.tenant_id, watermark.tenant_name)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={watermark.last_status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatWhen(watermark.last_run_at)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatNumber(watermark.rows_ingested)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">
                    {formatNumber(watermark.watermark_journal_number)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </XeroPanel>
  );
}
